/*
 * Counterpoint, 22 Oct 2026 (add on 2). Story viewer for the reels row (snippets/cp-stories.liquid).
 * A tile opens its row's native <dialog> with showModal(), so focus moves in and Esc closes it; focus goes back to
 * the tile on close and the page behind is held by the theme's scroll lock events. Each reel plays through and the
 * next one follows; after the last, the viewer closes. Tap the right side for next and the left side for back,
 * press and hold to pause, arrow keys and the on screen arrows on desktop, swipe down or tap the backdrop to close.
 * Sound starts off; once turned on it stays on for the next reel. The video gets its URL only when a story opens.
 */
(function () {
  'use strict';

  var soundOn = false;
  var HOLD_MS = 220;

  function Viewer(root) {
    this.root = root;
    this.dialog = root.querySelector('[data-cp-story-viewer]');
    this.video = this.dialog.querySelector('[data-cp-story-video]');
    this.frame = this.dialog.querySelector('[data-cp-story-frame]');
    this.fills = this.dialog.querySelectorAll('.cp-story-viewer__fill');
    this.sound = this.dialog.querySelector('[data-cp-story-sound]');
    this.tiles = root.querySelectorAll('[data-cp-story-open]');
    this.index = 0;
    this.opener = null;
    this.raf = null;
    this.held = false;
    this.holdTimer = null;
    this.bind();
  }

  Viewer.prototype.bind = function () {
    var self = this;

    this.root.addEventListener('click', function (e) {
      var tile = e.target.closest('[data-cp-story-open]');
      if (tile && self.root.contains(tile) && !self.dialog.contains(tile)) {
        e.preventDefault();
        self.open(parseInt(tile.getAttribute('data-cp-story-open'), 10) || 0, tile);
      }
    });

    this.dialog.addEventListener('click', function (e) {
      if (e.target === self.dialog || e.target.classList.contains('cp-story-viewer__stage')) { self.dialog.close(); return; }
      if (e.target.closest('[data-cp-story-close]')) { self.dialog.close(); return; }
      if (e.target.closest('[data-cp-story-prev]')) { self.go(-1); return; }
      if (e.target.closest('[data-cp-story-next]')) { self.go(1); return; }
      if (e.target.closest('[data-cp-story-sound]')) { self.toggleSound(); return; }
    });

    this.dialog.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft') { e.preventDefault(); self.go(-1); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); self.go(1); }
    });

    this.dialog.addEventListener('close', function () { self.cleanup(); });

    // Tap zones: a short tap moves back or on; a press held past HOLD_MS pauses until released.
    // A swipe down anywhere on the frame closes the viewer.
    var x0 = 0, y0 = 0, down = false;
    this.frame.addEventListener('pointerdown', function (e) {
      if (e.target.closest('.cp-story-viewer__controls')) return;
      down = true;
      x0 = e.clientX; y0 = e.clientY;
      self.held = false;
      clearTimeout(self.holdTimer);
      self.holdTimer = setTimeout(function () {
        self.held = true;
        self.video.pause();
        self.dialog.setAttribute('data-paused', '');
      }, HOLD_MS);
    });
    function release(e, cancelled) {
      if (!down) return;
      down = false;
      clearTimeout(self.holdTimer);
      var dx = e.clientX - x0, dy = e.clientY - y0;
      if (self.held) {
        self.held = false;
        self.dialog.removeAttribute('data-paused');
        if (dy > 80 && Math.abs(dy) > Math.abs(dx)) { self.dialog.close(); return; }
        self.resume();
        return;
      }
      if (cancelled) return;
      if (dy > 80 && Math.abs(dy) > Math.abs(dx)) { self.dialog.close(); return; }
      if (Math.abs(dx) > 12 || Math.abs(dy) > 12) return;
      var tap = e.target.closest('[data-cp-story-tap]');
      if (tap) self.go(parseInt(tap.getAttribute('data-cp-story-tap'), 10));
    }
    this.frame.addEventListener('pointerup', function (e) { release(e, false); });
    this.frame.addEventListener('pointercancel', function (e) { release(e, true); });
    this.frame.addEventListener('contextmenu', function (e) { e.preventDefault(); });

    this.video.addEventListener('ended', function () { self.go(1); });

    document.addEventListener('visibilitychange', function () {
      if (document.hidden && self.dialog.open) self.video.pause();
      else if (!document.hidden && self.dialog.open && !self.held) self.resume();
    });
  };

  Viewer.prototype.open = function (i, opener) {
    if (this.dialog.open) return;
    this.opener = opener;
    this.dialog.showModal();
    document.dispatchEvent(new CustomEvent('theme:scroll:lock', { bubbles: true, detail: this.dialog }));
    var close = this.dialog.querySelector('[data-cp-story-close]');
    if (close) close.focus({ preventScroll: true });
    this.syncSound();
    this.play(i);
    this.tick();
  };

  Viewer.prototype.play = function (i) {
    this.index = i;
    var tile = this.tiles[i];
    var src = tile.getAttribute('data-cp-story-src');
    var poster = tile.getAttribute('data-cp-story-poster');
    if (poster) this.video.setAttribute('poster', poster); else this.video.removeAttribute('poster');
    this.video.muted = !soundOn;
    this.video.setAttribute('src', src);
    this.dialog.setAttribute('data-story', String(i + 1));
    for (var b = 0; b < this.fills.length; b++) this.fills[b].style.transform = 'scaleX(' + (b < i ? 1 : 0) + ')';
    this.resume();
  };

  Viewer.prototype.resume = function () {
    var p = this.video.play();
    var v = this.video;
    if (p && p.catch) p.catch(function () {
      // A browser that refuses sound without a fresh gesture still plays muted.
      if (!v.muted) { v.muted = true; soundOn = false; v.play().catch(function () {}); }
    });
  };

  Viewer.prototype.go = function (dir) {
    var next = this.index + dir;
    if (next >= this.tiles.length) { this.dialog.close(); return; }
    if (next < 0) { this.video.currentTime = 0; this.resume(); return; }
    this.play(next);
  };

  Viewer.prototype.toggleSound = function () {
    soundOn = !soundOn;
    this.video.muted = !soundOn;
    this.syncSound();
    if (soundOn && this.video.paused && !this.held) this.resume();
  };

  Viewer.prototype.syncSound = function () {
    if (this.sound) this.sound.setAttribute('aria-pressed', soundOn ? 'true' : 'false');
  };

  Viewer.prototype.tick = function () {
    var self = this;
    cancelAnimationFrame(this.raf);
    (function frame() {
      if (!self.dialog.open) return;
      var d = self.video.duration;
      var f = d && isFinite(d) ? Math.min(self.video.currentTime / d, 1) : 0;
      var fill = self.fills[self.index];
      if (fill) fill.style.transform = 'scaleX(' + f + ')';
      self.raf = requestAnimationFrame(frame);
    })();
  };

  Viewer.prototype.cleanup = function () {
    cancelAnimationFrame(this.raf);
    clearTimeout(this.holdTimer);
    this.held = false;
    this.dialog.removeAttribute('data-paused');
    this.video.pause();
    this.video.removeAttribute('src');
    this.video.load();
    document.dispatchEvent(new CustomEvent('theme:scroll:unlock', { bubbles: true }));
    if (this.opener) this.opener.focus({ preventScroll: true });
  };

  function initAll(scope) {
    var roots = (scope || document).querySelectorAll('[data-cp-stories]');
    for (var i = 0; i < roots.length; i++) {
      if (roots[i].cpStories) continue;
      roots[i].cpStories = new Viewer(roots[i]);
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { initAll(); });
  else initAll();
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
})();
